import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges, ElementRef, AfterViewInit, OnDestroy } from '@angular/core'
import { Router } from '@angular/router'
import { IonicModule, IonContent, IonInfiniteScroll } from '@ionic/angular'
import { UserService } from 'src/app/services/user/user.service'
import { TranslateConfigService } from 'src/app/services/translate-config.service'
import { CategoriesService } from 'src/app/services/api/categories.service'
import { PostsService } from 'src/app/services/posts/posts.service'
import { NotificationService } from 'src/app/services/api/notification.service'
import { Subscription } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { UtilsService } from 'src/app/services/utils/utils.service'
import { AppStorageService } from 'src/app/services/app-storage.service'
import { StorageKeys } from 'src/app/services/storage-keys'
import { CommonModule } from '@angular/common'
import { TranslateModule } from '@ngx-translate/core'
import { NoHeaderComponent } from '../no-header/no-header.component'
import { CategoryComponent } from '../category/category.component'
import { DialogComponent } from '../dialog/dialog.component'

@Component({
  selector: 'app-home-mobile',
  templateUrl: './home-mobile.component.html',
  styleUrls: ['./home-mobile.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, NoHeaderComponent, CategoryComponent, DialogComponent]
})
export class HomeMobileComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild(IonInfiniteScroll) infiniteScroll!: IonInfiniteScroll
  @ViewChild(IonContent) content!: IonContent
  @ViewChild('infiniteScroll', { static: false }) infiniteScrollElement!: ElementRef;

  @Input() items: any[] = []
  @Input() selectedCategory: any
  @Input() isLoading: boolean = false
  @Input() loadedAllCategories: boolean = false

  @Output() showAllCategories: EventEmitter<void> = new EventEmitter<void>()
  @Output() selectCategory: EventEmitter<any> = new EventEmitter<any>()
  @Output() loadMore: EventEmitter<any> = new EventEmitter<any>()
  @Output() refresh: EventEmitter<any> = new EventEmitter<any>()

  hasUnreadNotifications: boolean = false

  // Suscripciones
  private notificationCountSubscription: Subscription = new Subscription()
  private langSub?: Subscription
  private resetInfiniteScrollListener: ((event: Event) => void) | null = null

  // Propiedades adicionales para manejar subcategorías
  subcategories: any[] = []
  isLoadingSubcategories: boolean = false
  loadedAllSubcategories: boolean = false
  currentPage: number = 1
  itemsPerPage: number = 10
  viewingSubcategories: boolean = false

  // Propiedades para la opción "Ver todo"
  viewAllCategory = {
    id: 0,
    name: this.translateConfigService.getItemTranslated('HOME.VIEW_ALL'),
    imagen: '',
    image: '',
    isViewAll: true
  }

  viewAllSubcategory = {
    id: 0,
    name: this.translateConfigService.getItemTranslated('HOME.VIEW_ALL'),
    imagen: '',
    image: '',
    isViewAll: true
  }

  guest: boolean = false
  show: boolean = false

  constructor(
    private router: Router,
    private userService: UserService,
    public translateConfigService: TranslateConfigService,
    private categoriesService: CategoriesService,
    private utilsService: UtilsService,
    private postsService: PostsService,
    private notificationService: NotificationService,
    private storage: AppStorageService
  ) {}

  async ngOnInit() {
    this.guest = await this.userService.checkGuest();
    this.subscribeToNotificationCount();
    await this.syncWithLocalStorage();

    // Actualizar textos cuando cambie el idioma
    this.langSub = this.translateConfigService.onLanguageChange().subscribe(() => {
      this.viewAllCategory.name = this.translateConfigService.getItemTranslated('HOME.VIEW_ALL');
      this.viewAllSubcategory.name = this.translateConfigService.getItemTranslated('HOME.VIEW_ALL');
    });
  }

  ngOnDestroy() {
    // Limpiar suscripciones
    if (this.notificationCountSubscription) {
      this.notificationCountSubscription.unsubscribe();
    }

  this.langSub?.unsubscribe();

    // Remover event listener si existe
    if (this.resetInfiniteScrollListener) {
      document.removeEventListener('resetInfiniteScroll', this.resetInfiniteScrollListener);
      this.resetInfiniteScrollListener = null;
    }

    // Limpiar referencias del infinite scroll
    this.cleanupInfiniteScroll();
  }

  /**
   * Sincronizar estado con localStorage
   */
  private async syncWithLocalStorage() {
    try {
      const isSubcategoryStr = this.storage.get(StorageKeys.IS_SUBCATEGORY);

      if (isSubcategoryStr) {
        const isSubcategory = JSON.parse(isSubcategoryStr);
        this.viewingSubcategories = isSubcategory;

        if (this.viewingSubcategories && !this.selectedCategory) {
          const storedCategory = this.storage.get(StorageKeys.SELECTED_CATEGORY);
          if (storedCategory) {
            const parsed = JSON.parse(storedCategory);
            if (!parsed.translationKey && parsed.id === 0) parsed.translationKey = 'HOME.VIEW_ALL';
            this.selectedCategory = parsed;

            if (this.selectedCategory && this.selectedCategory.id) {
              await this.loadSubcategoriesInternal(this.selectedCategory);
            }
          }
        }
      }
    } catch (error) {
      // Resetear a estado por defecto en caso de error
      this.viewingSubcategories = false;
      this.storage.setJSON(StorageKeys.IS_SUBCATEGORY, false);
    }
  }

  /**
   * Se suscribe al contador de notificaciones reactivo
   */
  private subscribeToNotificationCount() {
    this.notificationCountSubscription = this.notificationService.notificationCount$
      .pipe(
        map(count => count > 0)
      )
      .subscribe({
        next: (hasNotifications) => {
          this.hasUnreadNotifications = hasNotifications;
        },
        error: (error) => {
          this.hasUnreadNotifications = false;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items'] && !changes['items'].firstChange) {
      this.updateInfiniteScrollState();
    }

    if (changes['selectedCategory']) {
      const storedCategory = this.storage.get(StorageKeys.SELECTED_CATEGORY);
      if (storedCategory) {
        this.selectedCategory = JSON.parse(storedCategory);
      }
    }

    if (changes['loadedAllCategories']) {
      this.updateInfiniteScrollState();
    }
  }

  ngAfterViewInit() {
    // Configurar el listener con referencia para poder removerlo después
    this.resetInfiniteScrollListener = () => {
      this.resetInfiniteScroll();
    };

    document.addEventListener('resetInfiniteScroll', this.resetInfiniteScrollListener);

    // Asegurar que el infinite scroll esté correctamente configurado
    setTimeout(() => {
      this.updateInfiniteScrollState();
    }, 100);
  }

  /**
   * Actualizar el estado del infinite scroll
   */
  private updateInfiniteScrollState() {
    setTimeout(() => {
      if (this.infiniteScroll) {
        const shouldDisable = this.viewingSubcategories
          ? this.loadedAllSubcategories
          : this.loadedAllCategories;

        this.infiniteScroll.disabled = shouldDisable;
      }
    }, 50);
  }

  /**
   * Limpiar referencias del infinite scroll
   */
  private cleanupInfiniteScroll() {
    if (this.infiniteScroll) {
      try {
        this.infiniteScroll.complete();
        this.infiniteScroll.disabled = true;
      } catch (error) {
      }
    }
  }

  // Método para manejar el evento de cargar más categorías
  loadMoreCategories(event: any) {
    if (this.viewingSubcategories) {
      this.loadMoreSubcategories(event);
    } else {
      this.loadMore.emit(event);
    }
  }

  // Método para cargar más subcategorías
  loadMoreSubcategories(event: any) {
    if (this.loadedAllSubcategories || !this.selectedCategory) {
      event.target.complete();
      return;
    }

    this.currentPage++;
    this.isLoadingSubcategories = true;

    this.categoriesService.getSubcategoriesByCategoryId(
      this.selectedCategory.id,
      this.currentPage,
      this.itemsPerPage
    ).subscribe({
      next: (response) => {
        if (response.subcategories && response.subcategories.length > 0) {
          this.subcategories = [...this.subcategories, ...response.subcategories];
        }

        this.loadedAllSubcategories = !response.pagination.hasMorePages;
        this.isLoadingSubcategories = false;
        event.target.complete();

        this.updateInfiniteScrollState();
      },
      error: (error) => {
        this.isLoadingSubcategories = false;
        event.target.complete();
      }
    });
  }

  // Método para manejar el evento pull-to-refresh
  refreshCategories(event: any) {
    if (this.viewingSubcategories && this.selectedCategory) {
      this.refreshSubcategories(event);
    } else {
      this.refresh.emit(event);
    }
  }

  // Método para refrescar subcategorías
  refreshSubcategories(event: any) {
    this.currentPage = 1;
    this.loadedAllSubcategories = false;

    this.categoriesService.getSubcategoriesByCategoryId(
      this.selectedCategory.id,
      1,
      this.itemsPerPage
    ).subscribe({
      next: (response) => {
        this.subcategories = response.subcategories || [];
        this.loadedAllSubcategories = !response.pagination.hasMorePages;
        event.target.complete();

        this.updateInfiniteScrollState();
      },
      error: (error) => {
        event.target.complete();
      }
    });
  }

  showAll() {
    this.viewingSubcategories = false;
    this.subcategories = [];
    this.loadedAllSubcategories = false;
    this.currentPage = 1;

    this.storage.setJSON(StorageKeys.IS_SUBCATEGORY, false);
    this.showAllCategories.emit();

    this.updateInfiniteScrollState();
  }

  select(category: any) {
    if (category.isViewAll) {
      if (this.viewingSubcategories) {
        this.navigateToViewAllInCategory();
      } else {
        this.navigateToViewAll();
      }
      return;
    }

    if (this.viewingSubcategories) {
  this.storage.setJSON(StorageKeys.SELECTED_SUBCATEGORY, category);
      this.storage.setJSON(StorageKeys.IS_SUBCATEGORY, true);
      this.navigateToListing(category);
    } else {
  // Ensure translationKey is present when storing view-all sentinel
  const toStore = Object.assign({}, category);
  if (!toStore.translationKey && toStore.id === 0) toStore.translationKey = 'HOME.VIEW_ALL';
  this.storage.setJSON(StorageKeys.SELECTED_CATEGORY, toStore);
      this.storage.setJSON(StorageKeys.IS_SUBCATEGORY, false);
      this.storage.remove(StorageKeys.SELECTED_SUBCATEGORY);
      this.loadSubcategories(category);
    }
  }

  // Método para cargar subcategorías de una categoría seleccionada
  loadSubcategories(category: any) {
    this.selectedCategory = category;
    this.selectCategory.emit(category);
    this.viewingSubcategories = true;
    this.storage.setJSON(StorageKeys.IS_SUBCATEGORY, true);

  const toStore = Object.assign({}, this.selectedCategory);
  if (!toStore.translationKey && toStore.id === 0) toStore.translationKey = 'HOME.VIEW_ALL';
  this.storage.setJSON(StorageKeys.SELECTED_CATEGORY, toStore);

  this.loadSubcategoriesInternal(category);
  }

  /**
   * Cargar subcategorías internamente - CORREGIDO: Eliminado .toPromise()
   */
  private loadSubcategoriesInternal(category: any) {
    this.isLoadingSubcategories = true;
    this.currentPage = 1;
    this.subcategories = [];
    this.loadedAllSubcategories = false;

    this.categoriesService.getSubcategoriesByCategoryId(
      category.id,
      1,
      this.itemsPerPage
    ).pipe(
      take(1) // Solo tomar un valor y completar
    ).subscribe({
      next: (response) => {
        this.subcategories = response.subcategories || [];
        this.loadedAllSubcategories = !response.pagination.hasMorePages;
        this.isLoadingSubcategories = false;

        this.updateInfiniteScrollState();
      },
      error: (error) => {
        this.isLoadingSubcategories = false;
      }
    });
  }

  // Método para reiniciar manualmente el infiniteScroll
  resetInfiniteScroll() {
    try {
      if (this.infiniteScroll) {
        this.infiniteScroll.complete();

        setTimeout(() => {
          this.updateInfiniteScrollState();
          document.dispatchEvent(new CustomEvent('infiniteScrollResetSuccess'));
        }, 150);

        return;
      }

      // Fallback para buscar por ID
      const infiniteScrollEl = document.getElementById('categoryInfiniteScroll');
      if (infiniteScrollEl) {
        this.resetInfiniteScrollElement(infiniteScrollEl);
        return;
      }

      // Último intento con querySelector
      setTimeout(() => {
        const scrollElement = document.querySelector('app-home-mobile ion-infinite-scroll');
        if (scrollElement) {
          this.resetInfiniteScrollElement(scrollElement as HTMLElement);
        }
      }, 300);

    } catch (error) {
    }
  }

  /**
   * Reiniciar elemento de infinite scroll
   */
  private resetInfiniteScrollElement(element: HTMLElement) {
    element.classList.remove('infinite-scroll-loading', 'infinite-scroll-enabled', 'infinite-scroll-disabled');

    // Forzar reflow
    void element.offsetWidth;

    const isDisabled = this.viewingSubcategories
      ? this.loadedAllSubcategories
      : this.loadedAllCategories;

    if (isDisabled) {
      element.classList.add('infinite-scroll-disabled');
    } else {
      element.classList.add('infinite-scroll-enabled');
    }

    document.dispatchEvent(new CustomEvent('infiniteScrollResetSuccess'));
  }

  // Método para navegar a la vista de listado sin filtrar por categoría
  navigateToViewAll() {
    this.storage.setJSON(StorageKeys.SELECTED_CATEGORY, {
      id: 0,
      name: this.translateConfigService.getItemTranslated('HOME.VIEW_ALL'),
      translationKey: 'HOME.VIEW_ALL'
    });
    this.storage.setJSON(StorageKeys.IS_SUBCATEGORY, false);
    this.storage.remove(StorageKeys.SELECTED_SUBCATEGORY);

    this.router.navigate(['/app/listing']);
  }

  // Método para navegar a la vista de listado con todas las subcategorías de la categoría seleccionada
  navigateToViewAllInCategory() {
    const currentCategory = this.storage.get(StorageKeys.SELECTED_CATEGORY);
    const categoryObj = currentCategory ? JSON.parse(currentCategory) : this.selectedCategory;

    if (!currentCategory) {
      this.storage.setJSON(StorageKeys.SELECTED_CATEGORY, this.selectedCategory);
    }

    this.storage.setJSON(StorageKeys.SELECTED_SUBCATEGORY, {
      id: 0,
  name: this.translateConfigService.getItemTranslated('HOME.VIEW_ALL'),
  translationKey: 'HOME.VIEW_ALL',
      categoryId: categoryObj.id
    });

    this.storage.setJSON(StorageKeys.IS_SUBCATEGORY, true);

    this.router.navigate(['/app/listing']);
  }

  // Método para navegar a la vista de listado con la subcategoría seleccionada
  navigateToListing(subcategory: any) {
    // Guardar la subcategoría seleccionada
    this.storage.setJSON(StorageKeys.SELECTED_SUBCATEGORY, subcategory);

    // Verificar si ya existe una categoría guardada en localStorage
    const currentCategory = this.storage.get(StorageKeys.SELECTED_CATEGORY);

    // Solo guardar la categoría padre si no existe una ya guardada
    if (!currentCategory) {
  const toStore = Object.assign({}, this.selectedCategory);
  if (!toStore.translationKey && toStore.id === 0) toStore.translationKey = 'HOME.VIEW_ALL';
  this.storage.setJSON(StorageKeys.SELECTED_CATEGORY, toStore);
    }

    // Indicar que estamos trabajando con una subcategoría
    this.storage.setJSON(StorageKeys.IS_SUBCATEGORY, true);

    this.router.navigate(['/app/listing']);
  }

  navigate(path: string) {
    this.router.navigate([path])
  }

  openModal() {
    this.show = !this.show
  }

  async setPostToCreate(type: number) {
    // Siempre guardar el tipo para preservar la intención
    this.postsService.setPostToCreate(type);

    // Rechequear estado de invitado en el momento del click
    const isGuest = await this.userService.checkGuest();

    if (!isGuest) {
      this.navigate('/create-post');
    } else {
      // Guardar destino para redirigir tras login
      this.storage.set(StorageKeys.REDIRECT_URL, '/create-post');
      this.openModal();
    }
  }

  confirm() {
    this.show = false
    setTimeout(async () => {

      const isWeb = await this.utilsService.detectIsWeb()

      this.router.navigate([!isWeb?'/selection': '/login'])
    }, 200)
  }

  navigateToNotifications() {
    if (!this.guest) {
      this.navigate('app/notifications');
    } else {
      this.openModal();
    }
  }
}
